// @ts-strict-ignore
import type {Component, PanelLifecycleContext} from 'panel';
import {Patch, produceWithPatches} from 'immer';

import {getCommandKey, keyEventSwitcher, KeyModifiers} from 'common/util/keyboard';
import {getReportName} from 'common/report/util';
import {getActiveElement} from 'common/util/focusable';
import {Subscribable} from 'common/types/context/subscribable';

/**
 * Defines an entry for an undoable change, in the future this could define
 * a fn that allows the consumer to call arbirtary code for undoing a change, for instance:
 * recovering a deleted entity once it has been deleted
 */
export type ChangeEntry = [change: Patch[], inverseChange: Patch[], metadata?: Obj];

export type ChangeCallback = (changes: Patch[], metadata?: Obj) => void;

/**
 * Provides a simple undo/redo keyboard mapper based on URL fragments
 *
 * @todo [pablo] this is currently enabled for internal projects, ship once supported in all core reports
 */
export class HistoryManager implements PanelLifecycleContext, Subscribable {
  mixpanel: TODO = null;
  connectedComponents: Set<Component<any>> = new Set();
  subscriptions = [];
  private isUndoRedoDisabled = false;
  private historyIndex = -1;
  private changes: ChangeEntry[] = [];
  protected applyChanges: ChangeCallback = null;

  constructor({mixpanel}: {mixpanel?: TODO} = {}) {
    this.mixpanel = mixpanel;
    document.addEventListener(`keydown`, this.operatingSystemKeySwitcher);
  }

  subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((subscription) => subscription !== callback);
    return this;
  }

  bindToComponent(component: Component<any>) {
    this.connectedComponents.add(component);
  }

  unbindFromComponent(component: Component<any>) {
    this.connectedComponents.delete(component);
  }

  enableUndoRedo() {
    this.isUndoRedoDisabled = false;
  }

  disableUndoRedo() {
    this.isUndoRedoDisabled = true;
  }

  trackAction(action: string, ev: KeyboardEvent) {
    const fromKeyboard = Boolean(ev);
    const activeElement = getActiveElement();
    this.mixpanel?.track(action, {
      tab: getReportName(),
      'From Keyboard': fromKeyboard,
      'History Index': this.historyIndex,
      'Active Element': activeElement.tagName + `:` + activeElement.getAttribute(`elref`),
    });
  }

  updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }

    // This is one of the edge cases of react context + panel context.
    // So what happens is
    // assume we are on bookmarkURL (A)
    //
    // 1. undo / redo
    // 2. report-editor update to new bookmarkURLSlug (B)
    // 3. HistoryManagerProvider.forceUpdate
    // 4. StandardModeReact render (bookmarkURLSlug in StandardModeReact is (A))
    // 5. report-editor state is B, but StandardModeReact tells it the bookmarkURL is A, so report-editor tries to refetch bookmarkURL A.
    // 6. StandardModeReact.handleUpdateFromReportEditor (with correct new bookmarkURLSlug (B))
    //
    // the solution is quite simple. just wrap the react based "subscription" in requestAnimationFrame so that step 3 up there happens after step 6.
    // Hopefully the issue intermediate hack will be addressed after we fully move report-editor to react as well.
    requestAnimationFrame(() => {
      for (const subscription of this.subscriptions) {
        subscription();
      }
    });
  }

  get canApplyRedo() {
    return this.historyIndex + 1 < this.changes.length;
  }

  get canApplyUndo() {
    return this.historyIndex >= 0;
  }

  /**
   * Applies updates to a base object and generates JSON serializable fields
   * defining changes that can be applied on undo/redo or in the future persisted to a report
   * so that version history is preserved, this can also be the base for collaborative report editing
   *
   * @see https://immerjs.github.io/immer/patches
   * @param source base object to modify
   * @param updates changes to be applied to the source, a delta will be computed against the source
   * @param addHistoryEntry when set, adds an entry in the history stack
   */
  public applyUpdates<ResultType = Obj>({
    source,
    updates,
    addHistoryEntry = true,
    metadata,
  }: {
    source: ResultType;
    updates: Obj;
    addHistoryEntry?: boolean;
    metadata?: Obj;
  }): [result: ResultType, changeEntry: ChangeEntry] {
    const [result, patches, inverseChanges] = produceWithPatches<ResultType>(source, (draft) => {
      // this function assumes the updates passed in here reflect a desired state and not
      // a delta that will lead into a desired stated, with that in mind, we mark fields that
      // are not in the desired stated as deletions on the diff
      for (const key of Object.keys(source)) {
        if (key in updates === false) {
          delete draft[key];
        }
      }

      Object.assign(draft, updates);
    });

    if (patches.length && addHistoryEntry) {
      this.pushEntry([patches, inverseChanges, metadata]);
    }

    return [result, [patches, inverseChanges]];
  }

  get canUseUndoRedo() {
    return this.applyChanges !== null && !this.isUndoRedoDisabled;
  }

  /**
   * Starts an Undo/Redo history, for cases where we stack un-doable UI, the consumer
   * can pass a change history so that context is persisted across layers, example:
   * Editing a custom event within the modal can be un-doable, but these changes are not
   * to affect the underlying report
   */
  setup(applyChanges: ChangeCallback, changes?: ChangeEntry[]) {
    this.reset(changes);
    this.applyChanges = applyChanges;
  }

  /**
   * Detaches the history manager from any consumer
   */
  teardown() {
    this.reset();
    this.applyChanges = null;
  }

  /**
   * Cleans the history stack or sets it to some
   * starting state, useful for stacking undo/redo layers
   * @param changes - defines a starting history stack
   */
  reset(changes: ChangeEntry[] = []) {
    // intentionally allow this to be -1
    // to indicate an empty starting state
    this.historyIndex = changes.length - 1;
    this.changes = changes;
  }

  undo(ev?: KeyboardEvent) {
    if (this.canApplyUndo && this.canUseUndoRedo) {
      const [, targetChange, metadata] = this.changes[this.historyIndex];
      this.historyIndex -= 1;
      this.trackAction(`Undo Report Change`, ev);
      this.updateComponents();
      this.applyChanges(targetChange, metadata);
    }
  }

  redo(ev?: KeyboardEvent) {
    if (this.canApplyRedo && this.canUseUndoRedo) {
      const [targetChange, , metadata] = this.changes[this.historyIndex + 1];
      this.historyIndex += 1;
      this.trackAction(`Redo Report Change`, ev);
      this.updateComponents();
      this.applyChanges(targetChange, metadata);
    }
  }

  pushEntry(change: ChangeEntry) {
    // whenever we make an edit and we are not in the bottom of the history stack
    // we drop updates from the current index forward, so that the next set of updates
    // populate the history stack
    if (this.canApplyRedo) {
      this.changes.splice(this.historyIndex + 1);
    }

    this.changes.push(change);
    this.historyIndex = this.changes.length - 1;
    this.updateComponents();
  }

  operatingSystemKeySwitcher = (ev: KeyboardEvent) => {
    const commandKey = getCommandKey();
    return keyEventSwitcher(
      {
        // undo
        [`${commandKey} + KeyZ`]: (keyboardEvent: KeyboardEvent) => this.undo(keyboardEvent),

        // redo
        [`${commandKey} + ${KeyModifiers.Shift} + KeyZ`]: (keyboardEvent: KeyboardEvent) => this.redo(keyboardEvent),
      },
      {stopPropagation: true},
    )(ev);
  };
}
